import {
  useCallback, useMemo,
} from 'react';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import {
  getAppleMapsNavigateToAddressLink, getGoogleMapsNavigateToAddressLink,
} from '~/_shared/utils/link/gisLink';
import { createRightSidebarContextMenuCallbackProps } from '~/customizeLocationPanel/roundButtonMenu/contextMenuStructure/editLocationCallbacks.helpers';
import { useProcessMarkersForRouting } from '~/map/map/lassoTool/optimizeRouteBetweenPoints/useProcessMarkersForRouting';
import { type RowsPerSpreadsheet } from '~/map/map/lassoTool/rowsPerSpreadsheet';
import { useDrawRadiusOnMarker } from '~/map/map/proximity/useDrawRadiusOnMarker';
import { usePublicMapSettingsSelector } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';

export const useToolLocationCallbacks = (
  spreadsheetRowId?: SpreadsheetRowId,
  locationAddress?: string | null,
) => {
  const { processSelectedData } = useProcessMarkersForRouting();
  const { addMarkerToProximityTool } = useDrawRadiusOnMarker();
  const isMapPresentational = useIsMapPresentationalSelector();
  const { routingDirections: isRoutingAllowed, radiusProximity: isProximityAllowed } = usePublicMapSettingsSelector();

  const navigate = useCallback(() => {
    if (locationAddress) {
      window.location.href = getGoogleMapsNavigateToAddressLink(locationAddress);
    }
  }, [locationAddress]);

  const navigateApple = useCallback(() => {
    if (locationAddress) {
      window.location.href = getAppleMapsNavigateToAddressLink(locationAddress);
    }
  }, [locationAddress]);

  const addToRouteCallback = useCallback(
    () => {
      if (!spreadsheetRowId?.spreadsheetId || !spreadsheetRowId?.rowId) {
        return;
      }
      const rowsPerSpreadsheets: RowsPerSpreadsheet[] = [
        {
          spreadsheetId: spreadsheetRowId.spreadsheetId,
          rows: [{
            id: spreadsheetRowId.rowId,
          }],
        },
      ];
      processSelectedData(rowsPerSpreadsheets);
    },
    [processSelectedData, spreadsheetRowId],
  );

  const drawRadiusCallback = useCallback(() => {
    if (!spreadsheetRowId?.spreadsheetId || !spreadsheetRowId?.rowId) {
      return;
    }
    addMarkerToProximityTool(spreadsheetRowId, ProximityType.DistanceRadius);

  }, [addMarkerToProximityTool, spreadsheetRowId]);

  const addDTPCallback = useCallback(() => {
    if (!spreadsheetRowId?.spreadsheetId || !spreadsheetRowId?.rowId) {
      return;
    }
    addMarkerToProximityTool(spreadsheetRowId, ProximityType.DriveTimePolygon);

  }, [addMarkerToProximityTool, spreadsheetRowId]);

  return useMemo(() => ({
    addDTPCallback: !isMapPresentational ? addDTPCallback : undefined,
    addToRouteCallback: (!isMapPresentational || isRoutingAllowed) ? addToRouteCallback : undefined,
    drawRadiusCallback: (!isMapPresentational || isProximityAllowed) ? drawRadiusCallback : undefined,
    navigate: locationAddress ? createRightSidebarContextMenuCallbackProps({ callback: navigate }) : undefined,
    navigateApple: locationAddress ? createRightSidebarContextMenuCallbackProps({ callback: navigateApple }) : undefined,
  }), [addDTPCallback, addToRouteCallback, drawRadiusCallback, isMapPresentational, isProximityAllowed, isRoutingAllowed, locationAddress, navigate, navigateApple]);
};
